<template>
  <div class="doc-field-value-manage">
    <a-row :gutter="16">
      <!-- 左侧：业务单据列表 -->
      <a-col :span="6">
        <a-card title="业务单据" :bordered="false" size="small">
          <a-table
            :columns="documentColumns"
            :data-source="documentList"
            :loading="loading.document"
            :pagination="false"
            :customRow="onDocumentRow"
            rowKey="code"
            size="small"
            class="clickable-table"
          >
            <span slot="name" slot-scope="text, record">
              <div
                :class="[
                  'doc-item',
                  selectedDocument && selectedDocument.code === record.code ? 'active' : '',
                ]"
              >
                {{ record.name }}
                <span class="code-tag">{{ record.code }}</span>
              </div>
            </span>
          </a-table>
        </a-card>
      </a-col>

      <!-- 右侧：详情配置 -->
      <a-col :span="18">
        <div v-if="selectedDocument">
          <!-- 单据字段管理 -->
          <a-card :bordered="false" size="small" style="margin-bottom: 16px">
            <a-tabs v-model="activeGroupCode" @change="handleTabChange">
              <a-tab-pane v-for="group in fieldGroups" :key="group.code" :tab="`${group.name}字段`">
                <a-table
                  :columns="fieldColumns"
                  :data-source="group.fields"
                  :loading="loading.field"
                  :customRow="onTypeRow"
                  rowKey="id"
                  size="small"
                  class="clickable-table"
                >
                  <span slot="field_name" slot-scope="text, record">
                    <span
                      :class="{
                        'text-primary font-weight-bold':
                          selectedField && selectedField.id === record.id,
                      }"
                    >
                      {{ formatFieldDisplay(text) }}
                    </span>
                  </span>
                  <span slot="field_code" slot-scope="text">
                    {{ formatFieldDisplay(text) }}
                  </span>
                </a-table>
              </a-tab-pane>
            </a-tabs>
          </a-card>

          <!-- 字段可选值管理 -->
          <a-card :bordered="false" size="small" style="margin-top: 16px">
            <template slot="title">
              <span v-if="selectedField">
                字段可选值 -
                <small>{{ selectedField.field_name }} ({{ selectedDocument.name }})</small>
              </span>
              <span v-else>
                字段可选值
                <small>(请先选择上方单据字段)</small>
              </span>
            </template>
            <template slot="extra">
              <a-button
                type="primary"
                size="small"
                :disabled="!selectedField"
                @click="openValueModal()"
              >
                新增选项
              </a-button>
            </template>

            <a-table
              v-if="selectedField"
              :columns="valueColumns"
              :data-source="fieldValueList"
              :loading="loading.value"
              rowKey="id"
              size="small"
            >
              <span slot="action" slot-scope="text, record">
                <a @click.stop="openValueModal(record)">编辑</a>
                <a-divider type="vertical" />
                <a-popconfirm title="确定删除该选项吗？" @confirm="handleDeleteValue(record.id)">
                  <a @click.stop class="text-danger">删除</a>
                </a-popconfirm>
              </span>
            </a-table>
            <div v-else class="empty-holder">
              <a-empty description="请选择上方单据字段以查看可选值" />
            </div>
          </a-card>
        </div>
        <a-card
          v-else
          :bordered="false"
          style="height: 400px; display: flex; align-items: center; justify-content: center"
        >
          <a-empty description="请从左侧选择业务单据" />
        </a-card>
      </a-col>
    </a-row>

    <!-- 字段可选值弹窗 -->
    <a-modal
      v-model="valueModal.visible"
      :title="valueModal.form.id ? '编辑选项' : '新增选项'"
      @ok="handleSaveValue"
      :confirmLoading="loading.saving"
    >
      <a-form-model layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item label="字段值名称">
              <a-input v-model="valueModal.form.valueName" />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="字段值编码">
              <a-input v-model="valueModal.form.valueCode" :disabled="!!valueModal.form.id" />
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import http from '@/service/axios'
import api from '@/service/api'

export default {
  name: 'DocFieldValueManage',
  data() {
    return {
      documentList: [],
      selectedDocument: null,
      selectedField: null, // 当前选中的单据字段
      fieldList: [],
      childDocs: [],
      fieldValueList: [],
      activeGroupCode: '',
      loading: {
        document: false,
        field: false,
        value: false,
        saving: false,
      },
      documentColumns: [
        { title: '单据名称/编码', dataIndex: 'name', scopedSlots: { customRender: 'name' } },
      ],
      fieldColumns: [
        { title: '字段名称', dataIndex: 'field_name', scopedSlots: { customRender: 'field_name' } },
        { title: '字段编码', dataIndex: 'field_code', scopedSlots: { customRender: 'field_code' } },
      ],
      valueColumns: [
        { title: '选项名称', dataIndex: 'value_name' },
        { title: '选项编码', dataIndex: 'value_code' },
        { title: '操作', dataIndex: 'action', width: 120, scopedSlots: { customRender: 'action' } },
      ],
      valueModal: {
        visible: false,
        form: { id: '', valueName: '', valueCode: '' },
      },
    }
  },
  computed: {
    fieldGroups() {
      if (!this.fieldList || this.fieldList.length === 0) return []
      const groups = {}
      this.fieldList.forEach((field) => {
        const code = field.finance_document_code
        const name = field.finance_document_name || code
        if (!groups[code]) {
          groups[code] = {
            code,
            name,
            fields: [],
          }
        }
        groups[code].fields.push(field)
      })
      const orderedGroups = []
      if (this.selectedDocument && groups[this.selectedDocument.code]) {
        orderedGroups.push(groups[this.selectedDocument.code])
      }
      Object.keys(groups).forEach((code) => {
        if (this.selectedDocument && code === this.selectedDocument.code) return
        orderedGroups.push(groups[code])
      })
      return orderedGroups
    },
  },
  mounted() {
    this.getDocumentList()
  },
  methods: {
    // 1. 获取业务单据
    async getDocumentList() {
      this.loading.document = true
      try {
        const res = await http({
          url: api.financeDocument,
          data: { page_no: 1, page_size: 100, level: 0 },
        })
        this.documentList = res.result?.list || []
        if (this.documentList.length > 0) {
          this.selectDocument(this.documentList[0])
        }
      } finally {
        this.loading.document = false
      }
    },
    onDocumentRow(record) {
      return {
        on: { click: () => this.selectDocument(record) },
      }
    },
    async selectDocument(doc) {
      this.selectedDocument = doc
      this.selectedField = null // 切换单据时清空选中的字段
      this.fieldValueList = []
      this.activeGroupCode = ''
      this.getDocFieldList()
    },
    formatFieldDisplay(text) {
      if (!text || typeof text !== 'string') return text
      return text.includes('.') ? text.substring(text.indexOf('.') + 1) : text
    },

    // 2. 单据字段逻辑
    async getDocFieldList() {
      const childRes = await http({
        url: api.financeDocument,
        data: { pageNo: 1, pageSize: 1000, parentCode: this.selectedDocument.code },
      })
      this.childDocs = childRes.result.list || []
      const docCodes = [this.selectedDocument.code, ...this.childDocs.map((d) => d.code)]
      this.loading.field = true
      try {
        const res = await http({
          url: api.getFinanceDocumentField,
          data: { financeDocumentCodeList: docCodes, page_no: 1, page_size: 100 },
        })
        this.fieldList = res.result?.list || []
        // 初始化 tab 并自动选中第一个字段
        if (this.fieldGroups.length > 0) {
          this.activeGroupCode = this.fieldGroups[0].code
          if (this.fieldGroups[0].fields.length > 0) {
            this.selectField(this.fieldGroups[0].fields[0])
          } else {
            this.selectedField = null
            this.fieldValueList = []
          }
        } else {
          this.activeGroupCode = ''
          this.selectedField = null
          this.fieldValueList = []
        }
      } finally {
        this.loading.field = false
      }
    },
    onTypeRow(record) {
      return {
        on: { click: () => this.selectField(record) },
      }
    },
    selectField(field) {
      this.selectedField = field
      this.getFieldValueList()
    },
    handleTabChange(activeKey) {
      const group = this.fieldGroups.find((g) => g.code === activeKey)
      if (group && group.fields.length > 0) {
        this.selectField(group.fields[0])
      } else {
        this.selectedField = null
        this.fieldValueList = []
      }
    },

    // 3. 字段可选值逻辑 (必须基于 selectedField)
    async getFieldValueList() {
      if (!this.selectedField) return
      this.loading.value = true
      try {
        const res = await http({
          url: api.getFinanceDocumentFieldValue,
          data: {
            financeDocumentCode:
              this.selectedField.finance_document_code || this.selectedDocument.code,
            fieldCode: this.selectedField.field_code,
          },
          cancelDuplicate: true,
        })
        this.fieldValueList = res.result || []
      } finally {
        this.loading.value = false
      }
    },
    openValueModal(record) {
      if (!this.selectedField) return this.$message.warning('请先选择单据字段')
      this.valueModal.form = record
        ? {
            id: record.id,
            valueName: record.value_name,
            valueCode: record.value_code,
          }
        : { id: '', valueName: '', valueCode: '' }
      this.valueModal.visible = true
    },
    async handleSaveValue() {
      if (!this.valueModal.form.valueName || !this.valueModal.form.valueCode) {
        return this.$message.warning('请填写必填项')
      }
      this.loading.saving = true
      try {
        await http({
          url: api.createOrUpdateDocumentFieldValue,
          data: {
            ...this.valueModal.form,
            financeDocumentCode:
              this.selectedField.finance_document_code || this.selectedDocument.code,
            fieldCode: this.selectedField.field_code,
          },
        })
        this.$message.success('操作成功')
        this.valueModal.visible = false
        this.getFieldValueList()
      } finally {
        this.loading.saving = false
      }
    },
    async handleDeleteValue(id) {
      await http({ url: api.deleteDocumentFieldValue, data: { id } })
      this.$message.success('删除成功')
      this.getFieldValueList()
    },
  },
}
</script>

<style lang="scss" scoped>
.doc-field-value-manage {
  padding: 16px;
  background: #f0f2f5;
  min-height: 100vh;

  .clickable-table {
    :deep(.ant-table-row) {
      cursor: pointer;
    }
  }

  .doc-item {
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.3s;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.active {
      background: #e6f7ff;
      color: #1890ff;
      font-weight: bold;
    }

    .code-tag {
      font-size: 11px;
      color: #999;
      font-weight: normal;
    }
  }

  .text-primary {
    color: #1890ff;
  }
  .font-weight-bold {
    font-weight: bold;
  }
  .text-danger {
    color: #ff4d4f;
  }
  .disabled {
    color: #ccc;
    cursor: not-allowed;
  }

  .empty-holder {
    padding: 40px 0;
    background: #fff;
  }
}
</style>
